import React, { useState, useEffect } from 'react';
import { api } from '@/api/apiClient';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { 
  Search,
  Heart,
  CheckCircle2,
  Clock,
  XCircle,
  DollarSign
} from 'lucide-react';
import { format } from 'date-fns';

export default function EmployerEnrollments() {
  const [enrollments, setEnrollments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [products, setProducts] = useState([]);
  const [employers, setEmployers] = useState([]);
  const [selectedEmployerId, setSelectedEmployerId] = useState('');
  const [isSystemAdmin, setIsSystemAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    init();
  }, []);

  const init = async () => {
    try {
      const user = await api.auth.me();

      const systemAdmin = user?.user_role === 'admin' || user?.role === 'ADMIN';
      setIsSystemAdmin(systemAdmin);

      if (systemAdmin) {
        const employersData = await api.entities.Employer.list();
        setEmployers(employersData);
        const defaultEmployerId = employersData?.[0]?.id || '';
        setSelectedEmployerId(defaultEmployerId);
        if (defaultEmployerId) {
          await loadData(defaultEmployerId, true);
        } else {
          setEnrollments([]);
          setEmployees([]);
          setProducts([]);
        }
        return;
      }
      
      if (user.employer_id) {
        await loadData(user.employer_id, false);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadData = async (employerId, systemAdmin) => {
    const [enrollmentsData, employeesData, productsData] = await Promise.all([
      api.entities.Enrollment.filter({ employer_id: employerId }),
      api.entities.Employee.filter({ employer_id: employerId }),
      systemAdmin ? api.entities.BenefitProduct.filter({ employer_id: employerId }) : api.entities.BenefitProduct.list()
    ]);
    setEnrollments(enrollmentsData);
    setEmployees(employeesData);
    setProducts(productsData);
  };

  const handleEmployerChange = async (value) => {
    setSelectedEmployerId(value);
    setLoading(true);
    try {
      await loadData(value, true);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getEmployee = (employeeId) => employees.find(e => e.id === employeeId);
  const getProduct = (productId) => products.find(p => p.id === productId);

  const filteredEnrollments = enrollments.filter(enrollment => {
    const employee = getEmployee(enrollment.employee_id);
    const product = getProduct(enrollment.product_id);
    return (
      `${employee?.first_name} ${employee?.last_name}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product?.name?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  const getStatusBadge = (status) => {
    const styles = {
      active: { bg: 'bg-emerald-100', text: 'text-emerald-700', icon: CheckCircle2 },
      pending: { bg: 'bg-amber-100', text: 'text-amber-700', icon: Clock },
      cancelled: { bg: 'bg-red-100', text: 'text-red-700', icon: XCircle },
    };
    const style = styles[status] || styles.pending;
    const Icon = style.icon;
    
    return (
      <Badge className={`${style.bg} ${style.text}`}>
        <Icon className="w-3 h-3 mr-1" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const totalPremium = enrollments
    .filter(e => e.status === 'active')
    .reduce((sum, e) => sum + (e.premium_amount || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Enrollments</h1>
          <p className="text-slate-500 mt-1">
            Track employee benefit enrollments
          </p>
        </div>
        <Card className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white">
          <CardContent className="p-4 flex items-center gap-3">
            <DollarSign className="w-6 h-6" />
            <div>
              <p className="text-emerald-100 text-sm">Total Monthly Premiums</p>
              <p className="text-2xl font-bold">${totalPremium.toLocaleString()}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          {isSystemAdmin && (
            <div className="grid gap-2 mb-4">
              <Label>Employer</Label>
              <Select value={selectedEmployerId} onValueChange={handleEmployerChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select employer" />
                </SelectTrigger>
                <SelectContent>
                  {employers.map((e) => (
                    <SelectItem key={e.id} value={e.id}>
                      {e.company_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search by employee or product..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Employee</TableHead>
                <TableHead>Product</TableHead>
                <TableHead>Premium</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Effective Date</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredEnrollments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-12 text-slate-500">
                    No enrollments found
                  </TableCell>
                </TableRow>
              ) : (
                filteredEnrollments.map((enrollment) => {
                  const employee = getEmployee(enrollment.employee_id);
                  const product = getProduct(enrollment.product_id);
                  
                  return (
                    <TableRow key={enrollment.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium text-slate-900">
                            {employee?.first_name} {employee?.last_name}
                          </p>
                          <p className="text-sm text-slate-500">{employee?.user_email}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Heart className="w-4 h-4 text-emerald-500" />
                          {product?.name || 'Unknown'}
                        </div>
                      </TableCell>
                      <TableCell className="font-medium">
                        ${enrollment.premium_amount?.toFixed(2)}/mo
                      </TableCell>
                      <TableCell>{getStatusBadge(enrollment.status)}</TableCell>
                      <TableCell>
                        {enrollment.effective_date 
                          ? format(new Date(enrollment.effective_date), 'MMM d, yyyy')
                          : '-'
                        }
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}